const article = {
  slug: "pre-listing-package-template",
  cluster: "agent",
  title: "Pre-Listing Package Template: What to Include",
  description:
    "A pre-listing package template for real estate agents: the exact sections to include, what each one does, and how to assemble a winning version fast.",
  audience: "Real estate agents",
  funnelStage: "MOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "7 min read",
  tldr: "A strong pre-listing package includes a cover letter, your bio and track record, a marketing plan, a pricing/CMA section, a process timeline, references, and a clear next step. Send it before the appointment so the seller arrives already convinced. Tailor every section to that specific home and the seller's goal of netting the most money.",
  blocks: [
    {
      type: "p",
      spans: [
        "A pre-listing package is the document (PDF or printed booklet) you send a seller before the listing appointment so they walk in already trusting you. Below is a section-by-section template you can copy, with notes on what each part is actually doing and how to make it specific to one home instead of generic. The goal is simple: do the convincing before you arrive, so the appointment becomes a conversation about strategy rather than a sales pitch.",
      ],
    },
    {
      type: "callout",
      spans: [
        "Send the package 24-48 hours before the appointment. A seller who has read it walks in pre-sold on you; an unread package is just paper on the kitchen table.",
      ],
    },
    {
      type: "h2",
      text: "what should a pre-listing package include?",
      id: "what-to-include",
    },
    {
      type: "p",
      spans: [
        "Every strong package has the same backbone. Include these sections in this order, because it mirrors how a seller decides: who are you, can I trust you, what will you actually do, what is my home worth, and what happens next.",
      ],
    },
    {
      type: "table",
      headers: ["Section", "What it does", "Keep it to"],
      rows: [
        [
          "1. Personal cover letter",
          "Warm, specific intro addressed to this seller",
          "Half a page",
        ],
        ["2. About you / your team", "Establishes credibility and track record", "1 page"],
        ["3. Marketing plan", "Shows exactly how the home gets exposure", "1-2 pages"],
        [
          "4. Pricing & CMA preview",
          "Frames how price will be set (not the number yet)",
          "1-2 pages",
        ],
        ["5. The process & timeline", "Removes uncertainty about what happens when", "1 page"],
        ["6. References & reviews", "Social proof from past sellers", "Half a page"],
        ["7. What I need from you", "Clear, low-friction next step", "Short"],
      ],
    },
    {
      type: "h2",
      text: "how to write each section (annotated)",
      id: "section-by-section",
    },
    {
      type: "h3",
      text: "1. the cover letter",
      id: "cover-letter",
    },
    {
      type: "p",
      spans: [
        "Open with a short, personal note addressed to the seller by name and referencing their actual home. Two or three sentences: thank them for the opportunity, acknowledge what you noticed about the property, and state your single promise (for example, to help them net the most money with the least stress). Avoid a wall of text. This page sets the tone for everything after it.",
      ],
    },
    {
      type: "h3",
      text: "2. about you and your track record",
      id: "about-you",
    },
    {
      type: "p",
      spans: [
        "This is where you earn trust, but keep it focused on the seller's outcome, not your awards. Lead with the results that matter to a seller: how you market, how you negotiate, and the kind of homes you handle. If you are newer, lean on your brokerage's resources, your process, and your responsiveness instead of volume. New agents can build a credible version of this fast using the framing in ",
        {
          text: "how to get listings as a new agent",
          href: "/resources/how-to-get-listings-new-agent",
        },
        ".",
      ],
    },
    {
      type: "ul",
      items: [
        ["A short bio framed around how you help sellers, not your hobbies"],
        [
          "Proof points: recent sales, average days on market, or list-to-sale ratio (only real numbers you can stand behind)",
        ],
        ["What makes your approach different in one or two concrete sentences"],
        ["A professional headshot and your contact details"],
      ],
    },
    {
      type: "h3",
      text: "3. the marketing plan",
      id: "marketing-plan",
    },
    {
      type: "p",
      spans: [
        "This is the section sellers read most closely, because it answers 'what am I paying for?' Be specific and visual. List the concrete actions you take: professional photography, listing-description copywriting, photo sequencing, syndication to the major portals, social and email promotion, and your plan for the critical first week when buyer interest peaks. Generic promises ('I'll market it everywhere') lose to specifics. For a full breakdown of what belongs here, see ",
        {
          text: "how to build a listing marketing plan",
          href: "/resources/listing-marketing-plan",
        },
        ".",
      ],
    },
    {
      type: "callout",
      spans: [
        "Most homes that sell do so in their first few weeks on the market. Spell out exactly what you do in week one — that is the window that determines the final price.",
      ],
    },
    {
      type: "h3",
      text: "4. pricing and the CMA preview",
      id: "pricing-cma",
    },
    {
      type: "p",
      spans: [
        "Do not put a final price in the package. Instead, explain how you will arrive at one: that you use a comparative market analysis based on recent comparable sales, current competition, and the home's condition. This frames the appointment conversation and positions you as data-driven rather than someone who 'buys the listing' with a high number. If the seller is unfamiliar with the term, a one-paragraph primer pointing to ",
        {
          text: "what a CMA is",
          href: "/resources/what-is-a-cma",
        },
        " and ",
        {
          text: "how to price a listing with a CMA",
          href: "/resources/how-to-price-a-listing-cma",
        },
        " builds confidence that the eventual number is earned, not guessed.",
      ],
    },
    {
      type: "h3",
      text: "5. the process and timeline",
      id: "process-timeline",
    },
    {
      type: "p",
      spans: [
        "Sellers fear the unknown. Lay out the journey as a simple sequence so they can picture it: prep and staging, photography, going live, showings and feedback, offers and negotiation, under contract, inspection, and closing. A visual timeline works better than a paragraph. This single page removes most of the anxiety that makes sellers hesitate.",
      ],
    },
    {
      type: "ol",
      items: [
        ["Prep, repairs, and staging recommendations"],
        ["Professional photos and listing copy"],
        ["Go live across the major portals"],
        ["First-week showings and buyer feedback"],
        ["Review offers and negotiate"],
        ["Under contract, inspection, and appraisal"],
        ["Closing and keys"],
      ],
    },
    {
      type: "h3",
      text: "6. references and reviews",
      id: "references",
    },
    {
      type: "p",
      spans: [
        "Include two or three short testimonials from past sellers, ideally with the type of home or situation noted (for example, a seller relocating on a tight timeline). Real names or initials add credibility. Link to your full online reviews so a motivated seller can dig deeper. Social proof from people in a similar situation does more persuading than any claim you make about yourself.",
      ],
    },
    {
      type: "h3",
      text: "7. what i need from you (the next step)",
      id: "next-step",
    },
    {
      type: "p",
      spans: [
        "End with a clear, low-friction call to action: what to have ready for the appointment (recent improvements, any HOA documents, a list of questions) and how to reach you. Make the next step feel small and obvious. The package should close the loop, not leave the seller wondering what to do.",
      ],
    },
    {
      type: "h2",
      text: "how do you make the package specific to each home?",
      id: "make-it-specific",
    },
    {
      type: "p",
      spans: [
        "A generic template loses to a tailored one every time. The fastest way to personalize is to anchor each section to the actual property: reference the home's standout features in the cover letter, point the marketing plan at this home's best angles, and preview comps from this neighborhood in the pricing section. This is exactly where a tool helps — instead of rebuilding the package by hand for every appointment, ",
        {
          text: "Listino's Pre-Listing Prep",
          href: "/get-started",
        },
        " takes a Zillow URL or a few details and emails you a same-day listing prep with a CMA, marketing angles, and pricing guidance you can drop straight into your package. Pair it with the ",
        {
          text: "listing appointment checklist",
          href: "/resources/listing-appointment-checklist",
        },
        " and the deeper tactics in ",
        {
          text: "how to win the listing appointment",
          href: "/resources/how-to-win-the-listing-appointment",
        },
        ".",
      ],
    },
    {
      type: "stat",
      value: "Do the convincing before you arrive",
      label: "The job of a pre-listing package is to make the appointment a formality, not a pitch",
    },
    {
      type: "h2",
      text: "what is the difference between a pre-listing package and a listing presentation?",
      id: "package-vs-presentation",
    },
    {
      type: "p",
      spans: [
        "The package is what the seller reads beforehand, on their own time. The presentation is the live conversation at the appointment. They work as a pair: the package pre-sells you and frames the discussion, so the presentation can focus on the home's specific pricing and strategy rather than re-establishing trust from scratch. If you want a ready-made structure for the whole package, the ",
        {
          text: "pre-listing package template",
          href: "/resources/pre-listing-package-template",
        },
        " and the broader playbook in ",
        {
          text: "how to win the listing appointment",
          href: "/resources/how-to-win-the-listing-appointment",
        },
        " cover both halves.",
      ],
    },
  ],
  faqs: [
    {
      question: "What should a pre-listing package include?",
      answer: [
        {
          type: "p",
          spans: [
            "A complete pre-listing package includes a personal cover letter, an 'about you' section with your track record, a detailed marketing plan, a pricing and CMA preview (without the final number), a process timeline, two or three seller references, and a clear next step. Order matters: it should mirror how a seller decides — who you are, whether they can trust you, what you'll do, what their home is worth, and what happens next.",
          ],
        },
      ],
    },
    {
      question: "When should I send the pre-listing package to the seller?",
      answer: [
        {
          type: "p",
          spans: [
            "Send it 24 to 48 hours before the listing appointment. That gives the seller time to read it on their own and arrive already trusting you, so the appointment becomes a strategy conversation instead of a sales pitch. A package handed over at the meeting often goes unread.",
          ],
        },
      ],
    },
    {
      question: "Should I put the listing price in the pre-listing package?",
      answer: [
        {
          type: "p",
          spans: [
            "No. Include a section explaining how you'll arrive at a price using a comparative market analysis of recent comparable sales, current competition, and the home's condition — but save the actual number for the appointment. This positions you as data-driven and avoids the trap of 'buying the listing' with an inflated price before you've discussed strategy.",
          ],
        },
      ],
    },
    {
      question: "How is a pre-listing package different from a listing presentation?",
      answer: [
        {
          type: "p",
          spans: [
            "The pre-listing package is the document the seller reads before the meeting, on their own time. The listing presentation is the live conversation at the appointment. They work together: the package pre-sells you and frames the discussion, so the in-person meeting can focus on this specific home's pricing and strategy.",
          ],
        },
      ],
    },
    {
      question: "How can I create a pre-listing package quickly?",
      answer: [
        {
          type: "p",
          spans: [
            "Use a reusable template and personalize the home-specific sections per appointment. Listino's Pre-Listing Prep speeds this up by turning a Zillow URL or a few property details into a same-day listing prep — including a CMA, marketing angles, and pricing guidance — that you can drop straight into your package instead of building each one by hand.",
          ],
        },
      ],
    },
  ],
  related: [
    "how-to-get-listings-new-agent",
    "listing-marketing-plan",
    "what-is-a-cma",
    "how-to-price-a-listing-cma",
  ],
};

export default article;
